import React, {
  useState,
  useContext,
  useCallback,
  useRef,
  useMemo,
  useReducer,
} from 'react';
import {View} from 'react-native';
import {useIsFocused} from '@react-navigation/native';
import {RecyclerListView, LayoutProvider} from 'recyclerlistview';
import {Divider} from 'react-native-paper';
import moment from 'moment';

import sy from '~/styles';
import {
  Text,
  FloatingAddButton,
  ServiceRequestCard,
} from '~/components/controls';
import withDimensions from '~/components/hoc/with-dimensions';
import {DealerHeader} from '~/components/shared';
import UserContext from '~/components/context/UserContext';
import DateText from './components/DateText';
import Day from './components/Day';
import {getSectionRowHeight, getVisibleDateIndexFromScroll} from './helper';
import {
  NO_REQUESTS_HEIGHT,
  SECTION_TITLE_HEIGHT,
  TILE_HEIGHT,
} from './constants';
import {useCalendarData} from './hooks/useCalendarData';
import {useCalendarNavigation} from './hooks/useCalendarNavigation';
import PropTypes from 'prop-types';

class List extends React.Component {
  constructor(props) {
    super(props);
    this.ref = null;
  }

  render() {
    const {saveOffset, forwardRef, ...rest} = this.props;

    return (
      <RecyclerListView
        ref={(ref) => {
          forwardRef.current = ref;
          this.ref = ref;
        }}
        {...rest}
      />
    );
  }

  componentWillUnmount() {
    if (this.props.saveOffset.current === -1) {
      return;
    }

    this.props.saveOffset.current = this.ref.getCurrentScrollOffset();
  }
}

List.propTypes = {
  saveOffset: PropTypes.shape({
    current: PropTypes.number,
  }).isRequired,
  forwardRef: PropTypes.shape({
    current: PropTypes.any,
  }),
};

const Calendar = ({navigation, dimensions}) => {
  const isFocused = useIsFocused();

  const {window} = dimensions;
  const {height} = window;
  const width = Math.max(window.width > 512 ? 412 : window.width, 320);
  const breadth = width / 7;

  const [visibleIndex, setVisibleIndex] = useState(null);
  const [listOffset, setListOffset] = useState(null);
  const [, bumpRenderCount] = useReducer((count) => count + 1, 0);

  const listViewRef = useRef(null);
  const weekListViewRef = useRef(null);

  const {me} = useContext(UserContext);

  const forceRerender = useCallback(() => {
    bumpRenderCount();

    weekListViewRef.current?.forceRerender();
    listViewRef.current?.forceRerender();
  }, []);

  const {sections, dates, listDataProvider, weekDataProvider} = useCalendarData(
    {
      me,
      isFocused,
      setVisibleIndex,
      setListOffset,
      forceRerender,
      weekListViewRef,
      listViewRef,
    },
  );

  const {saveOffset} = useCalendarNavigation({
    navigation,
    isFocused,
    visibleIndex,
    forceRerender,
    weekListViewRef,
  });

  const layoutProvider = useMemo(() => {
    const provider = new LayoutProvider(
      (index) => index,
      (index, dim) => {
        if (!sections.current?.[index]) {
          dim.width = 0;
          dim.height = 0;
          return;
        }

        const {is_header, is_empty, is_last} = sections.current[index];
        dim.width = width;
        dim.height = getSectionRowHeight({is_header, is_empty, is_last});
      },
    );

    provider.shouldRefreshWithAnchoring = false;
    return provider;
  }, [width, sections]);

  const weekLayoutProvider = useMemo(
    () =>
      new LayoutProvider(
        (index) => index,
        (type, dim) => {
          dim.width = breadth;
          dim.height = breadth * 2;
        },
      ),
    [breadth],
  );

  const onScroll = useCallback(
    (e) => {
      const {contentOffset, contentSize} = e.nativeEvent;
      const dateIndex = getVisibleDateIndexFromScroll({
        contentOffset,
        contentSize,
        height,
        sections: listDataProvider.current.getAllData(),
        dates: dates.current,
        firstVisibleIndex: listViewRef.current?.findApproxFirstVisibleIndex(),
      });

      setVisibleIndex(dateIndex);
    },
    [height],
  );

  const onWeekDayRenderer = useCallback(
    (dataIndex, date) => (
      <View style={{flex: 1}}>
        <View
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            width: breadth,
            height: breadth,
          }}>
          <Text
            style={[
              sy.smallPlus,
              {color: '#828282', textTransform: 'capitalize'},
            ]}>
            {date.format('dd')[0]}
          </Text>
        </View>
        <View
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            width: breadth,
            height: breadth,
          }}>
          <Day
            selected={visibleIndex === dataIndex}
            selectedDealers={me?.dealer_selection}
            date={date.clone()}
            onChangeDate={() => {
              const key = date.format('YYYY-MM-DD');
              const listIndex = sections.current.findIndex(
                (request) => request.is_header && request.date === key,
              );

              listViewRef.current.scrollToIndex(listIndex, false);
            }}
          />
        </View>
      </View>
    ),
    [me, visibleIndex, breadth],
  );

  const onSectionRenderer = useCallback((type, data) => {
    const {is_header, is_empty, is_last, ...request} = data;
    return (
      <>
        {is_header && (
          <View
            style={[
              sy['px-4'],
              sy['justify-center'],
              {height: SECTION_TITLE_HEIGHT},
            ]}>
            <DateText date={moment(request.date, 'YYYY-MM-DD')} />
          </View>
        )}
        {is_empty ? (
          <View
            style={[
              sy['bg-white'],
              sy['mx-2'],
              sy['px-4'],
              sy['py-3.5'],
              sy['rounded-lg'],
              {height: NO_REQUESTS_HEIGHT},
            ]}>
            <Text style={[sy['text-lightgray']]}>Geen aanvragen</Text>
          </View>
        ) : (
          <ServiceRequestCard
            style={[
              is_last ? null : sy['mb-2'],
              sy['mx-2'],
              {height: TILE_HEIGHT},
            ]}
            key={request.id}
            show_time={true}
            {...request}
          />
        )}
      </>
    );
  }, []);

  return (
    <View style={sy.mainView}>
      {sections.current?.length > 0 && (
        <>
          <View style={[sy.headerView, sy['px-0'], sy['pt-4'], sy['pb-3']]}>
            <View style={{paddingHorizontal: 16}}>
              <DealerHeader
                titleStyle={{textTransform: 'capitalize'}}
                title={
                  visibleIndex
                    ? dates.current[visibleIndex]?.format('MMMM')
                    : ''
                }
              />
            </View>
            <View>
              <View
                style={{
                  flexDirection: 'row',
                  minHeight: breadth * 2,
                }}>
                <RecyclerListView
                  ref={weekListViewRef}
                  dataProvider={weekDataProvider.current}
                  layoutProvider={weekLayoutProvider}
                  rowRenderer={onWeekDayRenderer}
                  isHorizontal={true}
                  initialRenderIndex={visibleIndex - (visibleIndex % 7)}
                />
              </View>
            </View>
          </View>
          <Divider style={sy['bg-gray88']} />
          <View style={[sy.contentView, sy['bg-gray95'], sy['px-0']]}>
            <List
              key={listOffset}
              forwardRef={listViewRef}
              saveOffset={saveOffset}
              dataProvider={listDataProvider.current}
              layoutProvider={layoutProvider}
              rowRenderer={onSectionRenderer}
              scrollViewProps={{showsVerticalScrollIndicator: false}}
              scrollThrottle={100}
              onScroll={onScroll}
              renderAheadOffset={height / SECTION_TITLE_HEIGHT}
              initialOffset={listOffset}
            />
          </View>

          <FloatingAddButton />
        </>
      )}
    </View>
  );
};
Calendar.propTypes = {
  navigation: PropTypes.any,
  dimensions: PropTypes.any,
};

export default withDimensions(Calendar);
